import { Link } from 'react-router';
import type { TemplateListItemDto } from '@pams/shared';
import { StatusDot } from '@/components/StatusDot';
import { templateStateWord } from '@/lib/statusPresentation';
import { cn } from '@/lib/utils';
import { LIBRARY_COPY } from '../copy';
import { LIBRARY_PICK } from './paneFocus';
import {
  COLUMN_FILTERS,
  STATE_FILTERS,
  isFiltering,
  onlyTemplateOf,
  rowLineOf,
  type ColumnFilter,
  type StateFilter,
  type Tree,
  type TreeFilters,
  type TreeTemplate,
} from './templateTree';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B.1 §2] THE TEMPLATE TREE — the review site's `treeHTML`: Part, section, division, template.
 *
 *  ⚠️ A PART IS A `<details>` WHOSE OPEN STATE IS DERIVED WHILE RENDERING — open while filtering or
 *  while it holds what the pane shows, unless the person toggled it themselves. `onToggle` records a
 *  toggle only when the element's state DIFFERS from the derived one: a toggle event also fires when
 *  React opens it, and recording that would leave every Part open once a filter is cleared.
 *
 *  ⚠️ A ROW IS ONE LINE OF TITLE AND COUNT, THEN ONE LINE OF STATE IN WORDS — never a chip, because a
 *  list row says its state with a dot (`DESIGN_LANGUAGE.md`). Its checkbox is a SIBLING of the link,
 *  never inside it, and exists only for a template the SERVER calls ready.
 *  [amended TEMPLATE-FIND] A row is NUMBER, title and count: `t152` leads the first line, inside the one
 *  link, as MUTED TEXT — never a chip and never mono, which this language keeps for code chips — so the
 *  link's name and the row's state line are unchanged but for the number in front of the title.
 *
 *  ⚠️ [TEMPLATE-FIND] THE FILTER FINDS A TEMPLATE BY ITS NUMBER, AND ENTER OPENS THE ONE THAT IS LEFT —
 *  exactly as a pick in the tree would (`onOpen`: focus to the pane's heading, the tree's bar closed).
 *  The count says so while it holds, and only while filtering; Enter mid-composition (an IME) is the
 *  composition's, never ours. ⚠️ `isComposing` ALONE DOES NOT CATCH IT: Safari delivers the Enter that
 *  commits a composition AFTER `compositionend`, with `isComposing` false and keyCode 229 — the keyCode
 *  every browser gives a key an IME took — so the guard reads both.
 *
 *  ⚠️ THE FILTERS ARE NATIVE RADIOS, so arrow keys move between them with no code, and the count is a
 *  live region a screen reader hears after each change.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export type TreeCurrent =
  | { readonly kind: 'template'; readonly id: string; readonly part: string | null }
  | { readonly kind: 'section'; readonly key: string; readonly part: string | null }
  | { readonly kind: 'part'; readonly part: string }
  | null;

const scrollCurrent = (node: HTMLAnchorElement | null): void => {
  /* ⚠️ The optional CALL is required — jsdom has no `scrollIntoView`. See `HierarchyTable`. */
  node?.scrollIntoView?.({ block: 'nearest' });
};

const FilterGroup = <T extends string>({
  name,
  label,
  values,
  words,
  counts,
  value,
  onChange,
}: {
  name: string;
  label: string;
  values: readonly T[];
  words: Readonly<Record<T, string>>;
  counts: Readonly<Record<T, number>>;
  value: T;
  onChange: (next: T) => void;
}): React.ReactElement => (
  <fieldset className="flex flex-wrap items-center gap-1" data-testid={`tree-filter-${name}`}>
    <legend className="mb-1 text-xs text-muted-foreground">{label}</legend>
    {values.map((each) => (
      <label
        key={each}
        className={cn(
          'flex cursor-pointer items-center rounded-md border border-border px-2 py-0.5 text-xs',
          'has-[:checked]:border-input has-[:checked]:bg-active has-[:checked]:font-medium has-[:checked]:text-foreground',
          'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring',
        )}
      >
        <input
          type="radio"
          name={`tree-${name}`}
          value={each}
          checked={value === each}
          onChange={() => {
            onChange(each);
          }}
          className="sr-only"
        />
        {LIBRARY_COPY.filterCount(words[each], counts[each])}
      </label>
    ))}
  </fieldset>
);

const TreeRow = ({
  entry,
  setKey,
  search,
  current,
  checked,
  onSelect,
}: {
  entry: TreeTemplate;
  setKey: string;
  search: string;
  current: boolean;
  checked: boolean;
  onSelect: (id: string, checked: boolean) => void;
}): React.ReactElement => {
  const { item } = entry;
  const word = templateStateWord(item);
  const line = rowLineOf(entry);
  const ready = item.readiness.state === 'READY';
  return (
    <div className="flex items-start gap-1.5" data-testid="tree-template" data-key={item.key} data-state={entry.state}>
      {ready ? (
        <input
          type="checkbox"
          name={item.key}
          aria-label={LIBRARY_COPY.select(item.title)}
          checked={checked}
          onChange={(event) => {
            onSelect(item.id, event.target.checked);
          }}
          className="mt-1.5 size-4 shrink-0 accent-primary"
        />
      ) : (
        <span className="w-4 shrink-0" aria-hidden />
      )}
      <Link
        to={{ pathname: `/templates/${setKey}/t/${item.id}`, search }}
        state={LIBRARY_PICK}
        ref={current ? scrollCurrent : undefined}
        aria-current={current ? 'page' : undefined}
        className="flex min-w-0 flex-1 flex-col gap-0.5 rounded-md px-1.5 py-1 hover:bg-hover aria-[current=page]:bg-active"
        {...(item.readiness.state === 'BLOCKED' && item.readiness.reasons.length > 0
          ? { title: item.readiness.reasons.join('\n') }
          : {})}
      >
        <span className="flex items-baseline gap-2">
          <span
            className="min-w-8 shrink-0 text-xs tabular-nums text-muted-foreground"
            data-testid="tree-template-number"
          >
            {item.key}
          </span>
          <span className="min-w-0 flex-1 break-words text-foreground">{item.title}</span>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{item.dutyPointCount}</span>
        </span>
        <span
          className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground"
          data-testid="tree-template-line"
        >
          <StatusDot tone={word.tone} label={word.label} className="text-xs" />
          {line === '' ? null : <span className="min-w-0 truncate">· {line}</span>}
        </span>
      </Link>
    </div>
  );
};

export const TemplateTree = ({
  setKey,
  tree,
  filters,
  onFilters,
  stateCounts,
  columnCounts,
  current,
  toggled,
  onToggle,
  selected,
  onSelect,
  onOpen,
  search,
}: {
  setKey: string;
  tree: Tree;
  filters: TreeFilters;
  onFilters: (next: TreeFilters) => void;
  stateCounts: Readonly<Record<StateFilter, number>>;
  columnCounts: Readonly<Record<ColumnFilter, number>>;
  current: TreeCurrent;
  /** Parts the person opened or closed themselves — the derived state otherwise. */
  toggled: Readonly<Record<string, boolean>>;
  onToggle: (part: string, open: boolean) => void;
  selected: ReadonlySet<string>;
  onSelect: (id: string, checked: boolean) => void;
  /** [TEMPLATE-FIND] Opens a template exactly as a pick in the tree does — Enter in the filter calls it. */
  onOpen: (item: TemplateListItemDto) => void;
  /** The library's own search parameters (`?year=`), carried by every link so a pick keeps them. */
  search: string;
}): React.ReactElement => {
  const filtering = isFiltering(filters);
  const only = filtering ? onlyTemplateOf(tree) : null;
  const holds = (part: string): boolean => current !== null && current.part === part;
  return (
    <nav aria-label={LIBRARY_COPY.treeLabel} className="flex flex-col text-sm" data-testid="template-tree">
      <div className="sticky top-0 z-10 flex flex-col gap-2 border-b border-border bg-card p-3">
        <input
          type="search"
          aria-label={LIBRARY_COPY.filterLabel}
          placeholder={LIBRARY_COPY.filterPlaceholder}
          value={filters.text}
          onChange={(event) => {
            onFilters({ ...filters, text: event.target.value });
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || only === null) return;
            /* The composition's Enter: `isComposing` while it runs, keyCode 229 for Safari's after it ends. */
            if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return;
            event.preventDefault();
            onOpen(only.item);
          }}
          className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
          data-testid="tree-filter-text"
        />
        <FilterGroup
          name="state"
          label={LIBRARY_COPY.stateFilterLabel}
          values={STATE_FILTERS}
          words={LIBRARY_COPY.stateFilter}
          counts={stateCounts}
          value={filters.state}
          onChange={(state) => {
            onFilters({ ...filters, state });
          }}
        />
        <FilterGroup
          name="columns"
          label={LIBRARY_COPY.columnFilterLabel}
          values={COLUMN_FILTERS}
          words={LIBRARY_COPY.columnFilter}
          counts={columnCounts}
          value={filters.columns}
          onChange={(columns) => {
            onFilters({ ...filters, columns });
          }}
        />
        <p className="text-xs text-muted-foreground" aria-live="polite" data-testid="tree-count">
          {LIBRARY_COPY.count(tree.shown, tree.total, filtering)}
          {only === null ? null : ` — ${LIBRARY_COPY.enterOpens}`}
        </p>
      </div>

      <div className="flex flex-col gap-0.5 p-2">
        {tree.parts.length === 0 ? (
          <p className="px-1.5 py-2 text-muted-foreground" data-testid="tree-empty">
            {LIBRARY_COPY.noMatch}
          </p>
        ) : null}
        {tree.parts.map((part) => {
          const derived = filtering || holds(part.part);
          const open = toggled[part.part] ?? derived;
          const partCurrent = current?.kind === 'part' && current.part === part.part;
          return (
            <details
              key={part.part}
              open={open}
              onToggle={(event) => {
                if (event.currentTarget.open !== open) onToggle(part.part, event.currentTarget.open);
              }}
              data-testid={`tree-part-${part.part}`}
            >
              <summary className="flex cursor-pointer items-baseline justify-between gap-2 rounded-md px-1.5 py-1 font-medium text-heading hover:bg-hover">
                <span>{LIBRARY_COPY.part(part.part)}</span>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {filtering ? part.count : part.total}
                </span>
              </summary>
              <div className="flex flex-col gap-0.5 pb-2 pl-2">
                <Link
                  to={{ pathname: `/templates/${setKey}/part/${encodeURIComponent(part.part)}`, search }}
                  state={LIBRARY_PICK}
                  ref={partCurrent ? scrollCurrent : undefined}
                  aria-current={partCurrent ? 'page' : undefined}
                  className="rounded-md px-1.5 py-1 text-muted-foreground hover:bg-hover aria-[current=page]:bg-active aria-[current=page]:text-foreground"
                  data-testid="tree-part-link"
                >
                  {part.title}
                </Link>
                {part.sections.map((section) => {
                  const sectionCurrent = current?.kind === 'section' && current.key === section.key;
                  return (
                    <div key={section.key} className="flex flex-col gap-0.5" data-testid="tree-section">
                      <Link
                        to={{ pathname: `/templates/${setKey}/section/${encodeURIComponent(section.key)}`, search }}
                        state={LIBRARY_PICK}
                        ref={sectionCurrent ? scrollCurrent : undefined}
                        aria-current={sectionCurrent ? 'page' : undefined}
                        className="flex flex-col rounded-md px-1.5 py-1 hover:bg-hover aria-[current=page]:bg-active"
                        data-testid="tree-section-link"
                      >
                        <span className="text-xs font-medium text-muted-foreground">{section.code}</span>
                        <span className="text-foreground">{section.title}</span>
                      </Link>
                      {section.divisions.map((division) => (
                        <div key={division.code ?? '—'} className="flex flex-col gap-0.5 pl-2">
                          <p
                            className="px-1.5 pt-1 text-xs font-medium uppercase tracking-wide text-muted-foreground"
                            data-testid="tree-division"
                          >
                            {division.name ?? division.code ?? LIBRARY_COPY.noDivision}
                          </p>
                          {division.templates.map((entry) => (
                            <TreeRow
                              key={entry.item.id}
                              entry={entry}
                              setKey={setKey}
                              search={search}
                              current={current?.kind === 'template' && current.id === entry.item.id}
                              checked={selected.has(entry.item.id)}
                              onSelect={onSelect}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </details>
          );
        })}
      </div>
    </nav>
  );
};
